/**
 * M-01 管理ダッシュボード。入力・承認・月次処理の状況をまとめて示す。
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import { MonthNav } from '../../components/MonthNav.js';
import { Card, ErrorMessage, Kpi, Loading, Notice } from '../../components/ui.js';
import { currentMonth, formatMonthLabel } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

const STAGES = [
  { code: 'UNSUBMITTED_REPORTS', label: '入力・提出' },
  { code: 'UNAPPROVED_REPORTS', label: '承認' },
  { code: 'UNRESOLVED_ISSUES', label: '警告確認' },
  { code: 'NOT_CALCULATED', label: '試算・差分' },
];

export function DashboardPage(): React.JSX.Element {
  const [month, setMonth] = useState(currentMonth());
  const state = useAsync(() => endpoints.dashboard(month), [month]);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.data) return <Loading />;

  const { totals, priorities, conditions, closing, staff } = state.data;
  const closed = closing?.status === 'closed';

  return (
    <>
      <MonthNav
        month={month}
        onChange={setMonth}
        right={
          <Link className="btn btn--primary btn--sm" to="/admin/monthly">
            月次処理を開く
          </Link>
        }
      />

      <h1 className="page-heading">{formatMonthLabel(month)}の入力・承認・月次処理状況</h1>

      {closed && (
        <Notice tone="info" title="この月は確定済みです">
          日報の編集はできません。修正が必要な場合は確定を取り消してください。
        </Notice>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="対象スタッフ" value={`${totals.staffCount}名`} />
        <Kpi
          label="入力完了"
          value={`${totals.entryCompleted}名`}
          note={`未完了 ${totals.staffCount - totals.entryCompleted}名`}
        />
        <Kpi
          label="承認完了"
          value={`${totals.approvalCompleted}名`}
          note={`確認中 ${totals.staffCount - totals.approvalCompleted}名`}
        />
        <Kpi label="要確認" value={`${totals.needsAttention}名`} />
      </div>

      <Card title="月次処理の進捗">
        <ol className="grid gap-2 sm:grid-cols-4">
          {STAGES.map((stage, index) => {
            const condition = conditions.find((item) => item.code === stage.code);
            const done = condition?.satisfied ?? false;
            return (
              <li
                key={stage.code}
                className={`rounded-xl border p-3 ${
                  done ? 'border-brand-200 bg-brand-50' : 'border-line bg-white'
                }`}
              >
                <p className="text-[0.75rem] text-ink-muted">
                  ステップ{index + 1}
                  {done && <span className="ml-1 pill pill--ok">完了</span>}
                </p>
                <p className="font-semibold">{stage.label}</p>
                <p className="text-[0.78rem] text-ink-muted">{condition?.detail}</p>
              </li>
            );
          })}
        </ol>
      </Card>

      <Card title="優先して確認する項目">
        <ul className="divide-y divide-line text-sm">
          {priorities.map((priority) => (
            <li key={priority.code} className="flex items-center justify-between gap-3 py-2">
              <div>
                <p className="font-semibold">{priority.label}</p>
                <p className="text-[0.78rem] text-ink-muted">{priority.detail}</p>
              </div>
              <span className={`pill ${priority.count > 0 ? 'pill--warn' : 'pill--ok'}`}>
                {priority.count}件
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="スタッフ別の進捗" description="対応が必要なスタッフを先に表示します。">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>社員ID</th>
                <th>氏名</th>
                <th>所属</th>
                <th>承認グループ</th>
                <th className="text-right">入力</th>
                <th className="text-right">未承認</th>
                <th className="text-right">警告</th>
                <th>状態</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((person) => (
                <tr key={person.employeeId}>
                  <td>{person.employeeNumber}</td>
                  <td className="font-semibold">{person.name}</td>
                  <td className="text-ink-muted">{person.department}</td>
                  <td className="text-ink-muted">{person.approvalGroupName ?? '未割当'}</td>
                  <td className="text-right">
                    {person.enteredDays}/{person.targetDays}日
                  </td>
                  <td className="text-right">{person.unapprovedDays}件</td>
                  <td className="text-right">{person.unresolvedIssues}件</td>
                  <td>
                    <span className={`pill ${person.needsAttention ? 'pill--warn' : 'pill--ok'}`}>
                      {person.needsAttention ? '要確認' : '完了'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
